import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
// import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";

const {
  actions: { setAlert },
} = alertSlice;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const labelSx = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#8A94A6",
};

// ---- Email statement template (what the CPO receives) ------------------------
const StatementEmailPreview = ({ run, optionalMessage }) => {
  const calc = run.calc || {};
  const metrics = calc.metrics || [];

  return (
    <Box
      sx={{
        border: "1px solid #E4E8EF",
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      {/* Dark header */}
      <Box sx={{ bgcolor: "#0F1A2A", color: "#fff", px: 3, py: 2.4 }}>
        <Typography sx={{ ...labelSx, color: "#8FA0B8" }}>
          {run.brand || "Zynka Energy"}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: "20px", mt: 0.5 }}>
          Payout statement (single run)
        </Typography>
        <Typography sx={{ color: "#B7C2D2", fontSize: "13px", mt: 0.4 }}>
          {run.period} · BI {run.id}
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {optionalMessage?.trim() ? (
          <Typography sx={{ fontSize: "14px", color: "#2E3847", mb: 2, whiteSpace: "pre-wrap" }}>
            {optionalMessage.trim()}
          </Typography>
        ) : null}

        <Typography sx={{ fontSize: "14px", color: "#41505F" }}>Hello,</Typography>
        <Typography sx={{ fontSize: "14px", color: "#41505F", mt: 1.2, lineHeight: 1.6 }}>
          Below is your payout statement covering the period{" "}
          <b>{run.period}</b>. The net amount has been transferred to your
          registered bank account; the table at the bottom lists the bank
          reference(s) so you can match it on your statement.
        </Typography>

        {/* Summary cards */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2.5 }}>
          <Box sx={{ flex: 1, border: "1px solid #E7EBF1", borderRadius: "10px", p: 1.8 }}>
            <Typography sx={labelSx}>Beneficiary</Typography>
            <Typography sx={{ fontWeight: 700, color: "#2E3847", mt: 0.6, fontSize: "14px" }}>
              {run.cpo}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, border: "1px solid #E7EBF1", borderRadius: "10px", p: 1.8 }}>
            <Typography sx={labelSx}>Payments</Typography>
            <Typography sx={{ fontWeight: 700, color: "#2E3847", mt: 0.6, fontSize: "14px" }}>
              {run.payments || `${run.payees} · ${run.payees} bank-matched`}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              bgcolor: "#0F1A2A",
              borderRadius: "10px",
              p: 1.8,
              color: "#fff",
            }}
          >
            <Typography sx={{ ...labelSx, color: "#8FA0B8" }}>Net paid to you</Typography>
            <Typography sx={{ fontWeight: 800, mt: 0.6, fontSize: "18px" }}>
              {run.netPayout || run.total}
            </Typography>
          </Box>
        </Stack>

        {/* Calculation breakdown */}
        <Typography sx={{ ...labelSx, mt: 3, mb: 1 }}>Calculation breakdown</Typography>
        <Box sx={{ border: "1px solid #E7EBF1", borderRadius: "10px", overflow: "hidden" }}>
          <Box sx={{ bgcolor: "#F6F8FB", px: 2, py: 1.4 }}>
            <Typography sx={{ fontWeight: 700, color: "#2E3847", fontSize: "13px" }}>
              {run.period}
            </Typography>
            <Typography sx={{ color: "#6B7688", fontSize: "12px", mt: 0.3 }}>
              {run.summaryLine ||
                `${run.sessions} sessions · ${run.kwh} kWh delivered · Net payout ${
                  run.netPayout || run.total
                }`}
            </Typography>
          </Box>
          {metrics.length > 0 && (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...labelSx, borderBottom: "1px solid #EEF1F5" }}>
                      Metric
                    </TableCell>
                    {(calc.columns || ["Card", "Wallet", "RFID", "Total"]).map((c) => (
                      <TableCell
                        key={c}
                        sx={{ ...labelSx, textAlign: "right", borderBottom: "1px solid #EEF1F5" }}
                      >
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((m) => (
                    <TableRow key={m.label}>
                      <TableCell sx={{ fontSize: "13px", color: "#41505F", borderBottom: "1px solid #F3F5F9" }}>
                        {m.label}
                      </TableCell>
                      {m.values.map((v, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            fontSize: "13px",
                            textAlign: "right",
                            color: "#2E3847",
                            borderBottom: "1px solid #F3F5F9",
                          }}
                        >
                          {v}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ---- Main dialog -------------------------------------------------------------
const EmailPayoutStatementDialog = ({
  open,
  onClose,
  selectedRuns = [],
  onSend,
  onBuildPreview,
}) => {
  const dispatch = useDispatch();
  const [optionalMessage, setOptionalMessage] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);

  const runCount = selectedRuns.length;
  const previewRun = selectedRuns[0];
  const runIds = selectedRuns.map((r) => r.id);

  const resetAndClose = () => {
    setOptionalMessage("");
    setEmailInput("");
    setEmails([]);
    setShowPreview(false);
    setIsSending(false);
    setPreviewHtml(null);
    setIsBuilding(false);
    onClose();
  };

  // Build preview: fetch the same statement HTML the email will use, so what the
  // user sees is exactly what gets sent. Falls back to the local React preview
  // when no onBuildPreview handler is wired.
  const handleBuildPreview = async () => {
    if (!onBuildPreview) {
      setShowPreview(true);
      return;
    }
    try {
      setIsBuilding(true);
      const html = await onBuildPreview(runIds);
      setPreviewHtml(html || null);
      setShowPreview(true);
    } catch (err) {
      dispatch(
        setAlert({
          open: true,
          message: "Failed to build the preview. Please try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
    } finally {
      setIsBuilding(false);
    }
  };

  const addEmails = () => {
    const parts = emailInput
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!parts.length) return;

    const valid = [];
    const invalid = [];
    parts.forEach((e) => {
      if (EMAIL_REGEX.test(e)) {
        if (!emails.includes(e)) valid.push(e);
      } else {
        invalid.push(e);
      }
    });

    if (invalid.length) {
      dispatch(
        setAlert({
          open: true,
          message: `Invalid email format: ${invalid.join(", ")}`,
          severity: enums.SEVERITY.WARNING,
        })
      );
    }
    if (valid.length) {
      setEmails((prev) => [...prev, ...valid]);
      setEmailInput("");
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      addEmails();
    }
  };

  const removeEmail = (email) => setEmails((prev) => prev.filter((e) => e !== email));

  const canSend = emails.length > 0 && runCount > 0 && !isSending;

  const handleSend = async () => {
    if (!emails.length) {
      dispatch(
        setAlert({
          open: true,
          message: "Add at least one recipient email.",
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }
    try {
      setIsSending(true);
      await onSend?.({
        emails,
        message: optionalMessage.trim(),
        runIds: selectedRuns.map((r) => r.id),
      });
      dispatch(
        setAlert({
          open: true,
          message: `Payout statement sent to ${emails.length} recipient(s).`,
          severity: enums.SEVERITY.SUCCESS,
        })
      );
      resetAndClose();
    } catch (err) {
      dispatch(
        setAlert({
          open: true,
          message: "Failed to send payout statement. Please try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <MailOutlineRoundedIcon sx={{ color: "#2C6AF2" }} />
          <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
            Email payout run statement
          </Typography>
          <Chip
            label={`${runCount} run${runCount === 1 ? "" : "s"}`}
            size="small"
            sx={{ bgcolor: "#EFF3F7", color: "#677487", fontWeight: 700 }}
          />
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={resetAndClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} sx={{ minHeight: 340 }}>
          {/* Left: recipients + message */}
          <Box
            sx={{
              width: { xs: "100%", md: 300 },
              flexShrink: 0,
              p: 2.5,
              borderRight: { md: "1px solid #EEF1F5" },
              borderBottom: { xs: "1px solid #EEF1F5", md: "none" },
            }}
          >
            <Typography sx={labelSx}>Recipients</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="finance@cpo.com, owner@cpo.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              onBlur={addEmails}
              sx={{ mt: 0.8 }}
            />
            <Typography sx={{ fontSize: "11px", color: "#8A94A6", mt: 0.6 }}>
              Comma- or semicolon-separated for multiple. Press Enter to add.
            </Typography>
            {emails.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mt: 1.2 }}>
                {emails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    variant="outlined"
                    onDelete={() => removeEmail(email)}
                  />
                ))}
              </Stack>
            )}

            {/* Optional message + calc-sheet note hidden for now.
            <Typography sx={{ ...labelSx, mt: 3 }}>Optional message</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="e.g. Apologies for the delay — please find the payout statement attached."
              value={optionalMessage}
              onChange={(e) => setOptionalMessage(e.target.value)}
              sx={{ mt: 0.8 }}
            />
            <Typography sx={{ fontSize: "11px", color: "#8A94A6", mt: 0.6 }}>
              Inserted at the top of every email. Leave blank for default body.
            </Typography>

            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                color: "#8B5CF6",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              <AttachFileRoundedIcon fontSize="small" />
              Calculation sheet attached
            </Box>
            */}
          </Box>

          {/* Right: preview */}
          <Box sx={{ flex: 1, p: 2.5, bgcolor: "#FAFBFD", maxHeight: 520, overflowY: "auto" }}>
            {isBuilding ? (
              <Box
                sx={{
                  height: "100%",
                  minHeight: 300,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  color: "#8A94A6",
                }}
              >
                <CircularProgress size={26} />
                <Typography sx={{ fontSize: "13px" }}>Building preview…</Typography>
              </Box>
            ) : !showPreview ? (
              <Box
                sx={{
                  height: "100%",
                  minHeight: 300,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "#8A94A6",
                }}
              >
                <MailOutlineRoundedIcon sx={{ fontSize: 44, color: "#C3CBD6", mb: 1.5 }} />
                <Typography sx={{ fontSize: "14px", maxWidth: 340 }}>
                  Build a preview to see exactly what each CPO will receive before
                  any emails are sent.
                </Typography>
              </Box>
            ) : previewHtml ? (
              // Exact HTML the email will send — single source of truth.
              <Box
                sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E7EBF1", overflow: "hidden" }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : previewRun ? (
              <StatementEmailPreview run={previewRun} optionalMessage={optionalMessage} />
            ) : null}
          </Box>
        </Stack>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "12px", color: "#8A94A6" }}>
          One email per selected run with that run’s payout breakdown + calc sheet.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.2 }}>
          <Button variant="outlined" onClick={resetAndClose}>
            Close
          </Button>
          {!showPreview ? (
            <Button
              variant="contained"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={handleBuildPreview}
              disabled={runCount === 0 || isBuilding}
            >
              {isBuilding ? "Building…" : "Build preview"}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<SendRoundedIcon />}
              onClick={handleSend}
              disabled={!canSend}
            >
              {isSending
                ? "Sending…"
                : `Send to ${runCount} run${runCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPayoutStatementDialog;
